import { ComputedRef, Ref, ref } from 'vue'
import useRest from '../rest/useRest'
import { Model } from '../types/Model'
import { ValidationErrors } from '../errors/useValidationErrors'
import { StandardErrors } from '../errors/useStandardErrors'
import { LooseModelForm } from '../types/LooseModelForm'
import { ResourceNode } from '../types/ResourceNode'
import getConfig from '../plugin/getConfig'
import { Repository, useRepo } from 'pinia-orm'
import { CollectionResponse } from '../types/CollectionResponse'
import { GetModelsFormKeys } from '../types/GetModelsFormKeys'
import { useQueue } from '../queue/useQueue'
import { QueueTask } from '../queue/offlineQueue'
import { offlineCreateReachedServer } from '../queue/offlineCreateReachedServer'
import { useNetworkStatus } from 'src/shared/composables/useNetworkStatus/useNetworkStatus'
import { ulid } from 'ulid'
import { KeyValueStorer } from 'services/storage/KeyValueStorer'
import makeSqliteStorer from 'services/storage/local/makeSqliteStorer'
import arraySerializer from 'src/shared/serializers/arraySerializer'
import { useAuthState } from 'services/auth/useAuthState'

export type OnCreateCallback<ModelType extends Model> = (models: CollectionResponse<ModelType>) => void
export type OnQueuedCallback = (task: QueueTask) => void

export interface CreateResourceOptions<ModelType extends Model> {
  formDefaults?: () => Record<string, unknown>
  onCreate?: OnCreateCallback<ModelType>
  onQueued?: OnQueuedCallback
  notifyOnError?: boolean
  persist?: boolean
  queueIfOffline?: boolean
  localStorerMaker?: (dbKey:string, key: string) => KeyValueStorer
}

export interface UseCreateResourceReturn<ModelType extends Model> {
  create: (form?: LooseModelForm<ModelType>) => Promise<void>
  repo: Repository<ModelType>
  form: Ref<Partial<Record<GetModelsFormKeys<ModelType>, any>>>
  resetForm: () => void
  formDefaults: Ref<() => Record<string, unknown>>
  data: Ref<ResourceNode<ModelType> | null>
  creating: Ref<boolean>
  resource: Ref<ModelType | undefined>
  hasErrors: ComputedRef<boolean>
  validationErrors: Ref<ValidationErrors>
  standardErrors: Ref<StandardErrors<string | number>>
  hasValidationErrors: ComputedRef<boolean>
  hasStandardErrors: ComputedRef<boolean>
  onCreate: (callback: OnCreateCallback<ModelType>) => void
  onQueued: (callback: OnQueuedCallback) => void
}

const defaultOptions = {
  localStorerMaker: makeSqliteStorer,
  notifyOnError: true,
  persist: true,
  queueIfOffline: false,
}

function useCreateResource<ModelType extends typeof Model> (
  modelClass: ModelType,
  options: CreateResourceOptions<InstanceType<ModelType>> = {},
): UseCreateResourceReturn<InstanceType<ModelType>> {
  const repo = useRepo(modelClass)
  const authState = useAuthState()

  const entity = modelClass.entity
  options = Object.assign({}, defaultOptions, options)
  const storageIdentifier = `entities:${modelClass.entity}.records`

  let localStorageKey: string | undefined
  if (authState.tenantId.value && authState.orgId.value) {
    localStorageKey = `${authState.tenantId.value}-${authState.orgId.value}`
  } else {
    localStorageKey = undefined
  }

  const storer = (options.localStorerMaker && localStorageKey) ? options.localStorerMaker(localStorageKey, storageIdentifier) : undefined

  const networkStatus = useNetworkStatus()

  let queueService: ReturnType<typeof useQueue>
  if (options.queueIfOffline) {
    queueService = useQueue()
  }

  const config = getConfig()
  const errorNotifer = config.errorNotifiers?.create

  const formDefaults = ref(options.formDefaults || (() => { return {} }))
  const resource: Ref<InstanceType<ModelType> | undefined> = ref()

  const onCreateCallbacks: Ref<OnCreateCallback<InstanceType<ModelType>>[]> = ref([])
  const onQueuedCallbacks: Ref<OnQueuedCallback[]> = ref([])
  const form: Ref<Partial<Record<GetModelsFormKeys<InstanceType<ModelType>>, any>>> = ref({})

  function resetForm () {
    form.value = {}
  }

  resetForm()

  if (options.onCreate) {
    onCreateCallbacks.value.push(options.onCreate)
  }

  if (options.onQueued) {
    onQueuedCallbacks.value.push(options.onQueued)
  }

  const onCreate = (callback: OnCreateCallback<InstanceType<ModelType>>) => {
    onCreateCallbacks.value.push(callback)
  }

  const rest = useRest<ResourceNode<InstanceType<ModelType>>>(entity)

  async function enqueueTask (
    form: Partial<Record<GetModelsFormKeys<InstanceType<ModelType>>, any>>,
  ) {
    const task: QueueTask = {
      action: 'create',
      data: form,
      entity,
      resourceId: ulid(),
    }
    await queueService.enqueue(task)
    onQueuedCallbacks.value.forEach(callback => callback(task))
  }

  async function create (formParam?: LooseModelForm<InstanceType<ModelType>>) {
    const primaryForm = formParam || form.value
    const mergedForm = Object.assign({}, primaryForm, formDefaults.value())

    if (!networkStatus.connected.value && options.queueIfOffline && queueService) {
      await enqueueTask(mergedForm)
      return
    }

    // useRest.execute sets hasErrors then rethrows on transport/HTTP failure.
    // Catch so the enqueue-on-error branch below can run (queueIfOffline).
    try {
      await rest.execute('post', mergedForm)
    } catch {
      /* hasErrors already populated by useRest */
    }

    if (offlineCreateReachedServer({
      hasErrors: rest.hasErrors.value,
      responseData: rest.data.value,
    })) {
      if (rest.data.value.data) {
        resource.value = rest.data.value.data

        if (storer) {
          const resourcesFromStorageRaw = await storer.get()
          const resourcesFromStorage = resourcesFromStorageRaw ? arraySerializer.read(resourcesFromStorageRaw) as ModelType[] : undefined
          if (resourcesFromStorageRaw && resourcesFromStorageRaw !== 'undefined' && resourcesFromStorageRaw !== 'null') {
            if (resourcesFromStorage && typeof resourcesFromStorage === 'object') {
              resourcesFromStorage.push(resource.value)
              await storer.set(JSON.stringify(resourcesFromStorage))
            }
          }
        }

        if (options.persist) {
          repo.save(resource.value)
        }
      }

      resetForm()

      onCreateCallbacks.value.forEach(callback => {
        if (resource.value !== null) {
          callback(resource.value)
        }
      })
    } else {
      if (errorNotifer && options.notifyOnError) errorNotifer({ entityType: entity })
      if (options.queueIfOffline && queueService) {
        await enqueueTask(mergedForm)
      }
    }
  }

  return {
    create,
    formDefaults,
    resetForm,
    repo,
    form,
    data: rest.data,
    creating: rest.isFetching,
    resource,
    hasErrors: rest.hasErrors,
    validationErrors: rest.validationErrors,
    standardErrors: rest.standardErrors,
    hasValidationErrors: rest.hasValidationErrors,
    hasStandardErrors: rest.hasStandardErrors,
    onCreate,
  }
}

export { useCreateResource }
